import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, Loader } from "lucide-react";
import { formatUSPhone, phoneValidator } from "shared";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAppForm } from "@/hooks/useCreateForm";

export const Route = createFileRoute("/_authed/_org/settings/change-phone/")({
  component: RouteComponent,
});

const formValidator = z.object({
  code: z.string().length(6, "Verification code must be 6 digits"),
  newPhone: phoneValidator,
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const { trpc } = Route.useRouteContext();
  const {
    data: { user },
  } = useSuspenseQuery(trpc.auth.session.queryOptions());

  const { mutate: resendOTP, isPending: resendCodeSubmitting } = useMutation(
    trpc.auth.sendOTPChallenge.mutationOptions({
      onError: (error) => {
        toast.error(error.message);
      },
      onSuccess: () => {
        toast.success("Verification code sent");
      },
    }),
  );

  const { mutate: requestChangePhone, isPending: changePhoneSubmitting } =
    useMutation(
      trpc.auth.requestChangePhone.mutationOptions({
        onError: (error) => {
          toast.error(error.message);
        },
        onSuccess: () => {
          toast.success("Verification code sent to your new phone number");
          navigate({ to: "/settings/change-phone/confirm" });
        },
      }),
    );

  const form = useAppForm({
    defaultValues: {
      code: "",
      newPhone: "+1",
    },
    onSubmit: ({ value }) => {
      requestChangePhone({
        challengeCode: value.code,
        newPhone: value.newPhone,
      });
    },
    validators: {
      onSubmit: formValidator,
    },
  });

  return (
    <div className="p-4">
      <Button
        className="mb-4 inline-flex items-center"
        onClick={() => navigate({ to: "/settings" })}
        variant="ghost"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Settings
      </Button>

      <Card className="mx-auto max-w-md">
        <CardHeader>
          <CardTitle>Change Phone Number</CardTitle>
          <CardDescription>
            Change from {formatUSPhone(user?.phone)}. You will have to verify
            your new phone number.
          </CardDescription>
        </CardHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <CardContent className="space-y-4">
            <form.AppField
              children={(field) => {
                return (
                  <field.OTPField
                    description="Enter the 6-digit code sent to your current phone"
                    label="Verification Code"
                    required={true}
                  />
                );
              }}
              name="code"
            />

            <form.AppField
              children={(field) => {
                return (
                  <field.PhoneField
                    description="Enter your new US phone number"
                    label="New Phone Number"
                    placeholder="5555555555"
                    required={true}
                  />
                );
              }}
              name="newPhone"
            />
          </CardContent>

          <CardFooter className="flex flex-col gap-4">
            <Button
              className="w-full"
              disabled={changePhoneSubmitting}
              type="submit"
            >
              {changePhoneSubmitting ? "Submitting..." : "Submit"}
              {changePhoneSubmitting ? (
                <Loader className="ml-2 h-4 w-4 animate-spin" />
              ) : (
                <ArrowRight className="ml-2 h-4 w-4" />
              )}
            </Button>

            <Button
              className="font-medium text-primary text-sm"
              disabled={resendCodeSubmitting}
              onClick={() => resendOTP({ type: "phone" })}
              type="button"
              variant="ghost"
            >
              Resend verification code
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
